import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SearchApiService, GlobalSearchPersonResultDto } from '@core/api/services/search-api.service';
import { BehaviorSubject, Subject, catchError, debounceTime, distinctUntilChanged, finalize, map, switchMap, of, takeUntil } from 'rxjs';

@Component({ selector: 'app-member-selector', standalone: true, imports: [CommonModule, ReactiveFormsModule, MatAutocompleteModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressSpinnerModule], templateUrl: './member-selector.component.html', styleUrl: './member-selector.component.scss', changeDetection: ChangeDetectionStrategy.OnPush, providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MemberSelectorComponent), multi: true }] })
export class MemberSelectorComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() label = 'Miembros'; @Input() required = false; @Input() multiple = true; @Input() onlyMinistry = false; @Input() onlyEligibleSpiritualAssistanceVisitors = false; @Input() eligibilityDate: string | null = null; @Output() selectionChange = new EventEmitter<string[]>(); @Output() selectedMembersChange = new EventEmitter<GlobalSearchPersonResultDto[]>(); @Output() memberRemoved = new EventEmitter<GlobalSearchPersonResultDto>();
  @Input() selectedPeople: GlobalSearchPersonResultDto[] = [];
  readonly search = new FormControl('', { nonNullable: true });
  selected: GlobalSearchPersonResultDto[] = [];
  results: GlobalSearchPersonResultDto[] = [];
  readonly isLoading$ = new BehaviorSubject(false);

  private onChange = (_: string[]) => {}; private onTouched = () => {};
  private lastSearchTerm = '';
  private selectedIds: string[] = [];
  private selectedPeopleById = new Map<string, GlobalSearchPersonResultDto>();
  private readonly destroy$ = new Subject<void>();
  private searchCriteriaVersion = 0;

  get visibleResults(): GlobalSearchPersonResultDto[] {
    const selectedIds = new Set(this.selectedIds);
    return this.results.filter(person => !selectedIds.has(person.id));
  }
  constructor(private readonly searchApi: SearchApiService) {
    this.search.valueChanges.pipe(
      debounceTime(250),
      map(value => ({ value, criteriaVersion: this.searchCriteriaVersion })),
      distinctUntilChanged((previous, current) => previous.value === current.value && previous.criteriaVersion === current.criteriaVersion),
      switchMap(({ value, criteriaVersion }) => {
        const term = typeof value === 'string' ? value : this.lastSearchTerm;
        this.lastSearchTerm = term;
        if (term.trim().length > 0 && term.trim().length < 2) { this.isLoading$.next(false); return of({ people: [], criteriaVersion }); }
        this.isLoading$.next(true);
        return this.searchApi.search(term, [this.searchType], 10, true, this.onlyEligibleSpiritualAssistanceVisitors ? this.eligibilityDate ?? undefined : undefined).pipe(
          map(result => ({ people: result.people, criteriaVersion })),
          catchError(() => of({ people: [], criteriaVersion })),
          finalize(() => this.isLoading$.next(false))
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(({ people, criteriaVersion }) => {
      if (criteriaVersion === this.searchCriteriaVersion) this.results = people;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedPeople']) {
      this.selectedPeopleById = new Map((this.selectedPeople ?? []).map(person => [person.id, person]));
      this.refreshSelectedPeople();
    }

    if (changes['onlyEligibleSpiritualAssistanceVisitors'] || changes['eligibilityDate'] || changes['onlyMinistry']) this.refreshSearchForCriteriaChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(ids: string[] | null): void {
    this.selectedIds = this.normalizeIds(ids ?? []);
    this.refreshSelectedPeople();
  }

  registerOnChange(fn: (value: string[]) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  setDisabledState(disabled: boolean): void {
    if (disabled) this.search.disable({ emitEvent: false });
    else this.search.enable({ emitEvent: false });
  }

  add(person: GlobalSearchPersonResultDto): void {
    if (this.search.disabled) return;

    this.selectedPeopleById.set(person.id, person);
    this.selectedIds = this.multiple
      ? this.normalizeIds([...this.selectedIds, person.id])
      : [person.id];
    this.refreshSelectedPeople();
    this.lastSearchTerm = '';
    this.search.setValue('', { emitEvent: false });
    this.commit();
  }

  remove(id: string): void {
    if (this.search.disabled) return;

    const removed = this.selectedPeopleById.get(id);
    this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== id);
    this.refreshSelectedPeople();
    if (removed) this.memberRemoved.emit(removed);
    this.commit();
  }

  private refreshSelectedPeople(): void {
    this.selected = this.selectedIds
      .map(id => this.selectedPeopleById.get(id))
      .filter((person): person is GlobalSearchPersonResultDto => person !== undefined);
  }

  private normalizeIds(ids: string[]): string[] {
    return [...new Set(ids.filter(id => id.length > 0))];
  }

  private get searchType(): 'people' | 'active-ministry-bearers' | 'spiritual-assistance-eligible-visitors' {
    if (this.onlyEligibleSpiritualAssistanceVisitors) return 'spiritual-assistance-eligible-visitors';
    return this.onlyMinistry ? 'active-ministry-bearers' : 'people';
  }

  private refreshSearchForCriteriaChange(): void {
    this.searchCriteriaVersion++;
    this.results = [];
    if (this.search.value.trim().length >= 2) this.search.setValue(this.search.value);
  }

  private commit(): void {
    this.onChange(this.selectedIds);
    this.selectionChange.emit(this.selectedIds);
    this.selectedMembersChange.emit(this.selected);
    this.onTouched();
  }
}
